// The Auston campaign board, saving the decisions as they are taken.
//
// The board is still one big form — a campaign is SET UP in one pass, and the
// casting, the backdrop, the views and every row's shoe and note belong to that
// one save. But a status and a hemline are answers an operator gives while
// looking at a row, and making them wait for "Save board" is how a board drifts
// from what the person reading it already knows: a filter click, a reload or a
// different tab, and the answer is gone.
//
// So those two PATCH on change. They stay inside the form as well, so the
// button remains correct rather than fighting them.
//
// Markup:
//   <div data-controller="auston-board"
//        data-auston-board-url-value="<%= inline_update_path %>">
//     <select data-action="change->auston-board#save"
//             data-au-field="status" data-au-look="12">
//     <select data-action="change->auston-board#save"
//             data-au-field="hemline" data-au-style="p:5872">
//     <span data-au-flag></span>   <!-- anywhere inside the control's parent -->
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // The board's active filters: the status bar's counts are scoped to the body
  // filter, and a row that no longer matches the status filter has to say so.
  static values = { url: String, status: String, morphology: String }

  // What the server last confirmed for each self-saving control. A failed save
  // has to put the control BACK: a select still showing "Delivered" over a row
  // the database never moved is worse than no auto-save at all.
  connect() {
    this.element.querySelectorAll("[data-au-field]").forEach((el) => {
      el.dataset.auSaved = el.value
    })
  }

  save(event) {
    const select = event.target
    const value = select.value
    // The "mixed" placeholder is a description, not an answer.
    if (!value) return

    this.flag(select, "saving")

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.token()
      },
      body: JSON.stringify({
        field: select.dataset.auField,
        value: value,
        look_id: select.dataset.auLook,
        style: select.dataset.auStyle,
        morphology: this.morphologyValue
      })
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        select.dataset.auSaved = value
        this.flag(select, "saved")
        if (select.dataset.auField === "hemline") this.applyHemline(data)
        if (select.dataset.auField === "status") this.applyFilter(select, data.value)
        this.applyReadiness(data.looks)
        this.applyTally(data.tally)
      })
      .catch((r) => this.failed(select, r))
  }

  // Say what went wrong, and undo the control. The status code goes in the
  // tooltip so a server error can be told from a lost connection without
  // opening the console.
  failed(select, response) {
    const code = response && response.status ? response.status : "no reply"
    if (select.dataset.auSaved !== undefined) select.value = select.dataset.auSaved

    this.flag(select, "error")
    const mark = select.parentElement?.querySelector("[data-au-flag]")
    if (mark) mark.title = `Not saved (${code}) — the row was put back.`

    if (response && typeof response.json === "function") {
      response.json().then((body) => {
        if (mark && body && body.error) mark.title = `Not saved (${code}): ${body.error}`
      }).catch(() => {})
    }
  }

  // A floor-length hem covers the feet, so the shoe override stops meaning
  // anything — the same rule the server renders the row with.
  applyHemline(data) {
    (data.looks || []).forEach(({ id }) => {
      const shoe = this.element.querySelector(`[data-au-shoe="${id}"]`)
      if (shoe) shoe.disabled = data.floor_length
    })
  }

  // Both fields can clear or create a blocker, so the tick box follows the
  // server's verdict rather than a guess made here.
  applyReadiness(looks) {
    (looks || []).forEach(({ id, ready, blockers }) => {
      const box = this.element.querySelector(`input[name="look_ids[]"][value="${id}"]`)
      if (box) {
        box.disabled = !ready
        if (!ready) box.checked = false
        box.title = ready ? "" : `blocked: ${(blockers || []).join(", ")}`
      }

      const warn = this.element.querySelector(`[data-au-blockers="${id}"]`)
      if (warn) {
        warn.textContent = ready ? "" : `blocked: ${(blockers || []).join(", ")}`
        warn.hidden = ready
      }
    })
  }

  // The counts on the status chips are only true until someone changes a
  // status — which is the one thing this board is for. The server sends the
  // recount back with every save.
  applyTally(tally) {
    if (!tally) return

    this.element.querySelectorAll("[data-au-tally]").forEach((mark) => {
      mark.textContent = tally[mark.dataset.auTally] || 0
    })
  }

  // Filtered to one stage, a row answered out of that stage is no longer part
  // of the set. It stays put — removing it would move everything under the
  // cursor — and dims instead, so what is left reads as the filter.
  applyFilter(select, value) {
    if (!this.hasStatusValue || !this.statusValue) return

    select.closest(".au-row")?.classList.toggle("au-row--out", value !== this.statusValue)
  }

  flag(select, state) {
    const mark = select.parentElement?.querySelector("[data-au-flag]")
    if (!mark) return

    const marks = { saving: "…", saved: "✓", error: "✕ not saved" }
    mark.textContent = marks[state] || ""
    if (state !== "error") mark.title = ""
    mark.style.color = state === "error" ? "#b42318" : state === "saved" ? "#2ea067" : "#9aa0a6"
    if (state === "saved") {
      setTimeout(() => {
        if (mark.textContent === "✓") mark.textContent = ""
      }, 1500)
    }
  }

  token() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
